import React from 'react';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  PieChart,
  Table,
  StatusIndicator,
  Link
} from '@cloudscape-design/components';

// Dummy data for technical debt metrics
const debtMetrics = {
  overallScore: 76,
  criticalIssues: 12,
  majorIssues: 28,
  minorIssues: 45,
  debtRatio: 24, // percentage of codebase affected
};

// Dummy data for debt categories
const debtCategories = [
  {
    category: 'Code Duplication',
    instances: 15,
    severity: 'critical',
    impact: 'high',
    affectedFiles: 23
  },
  {
    category: 'Complex Methods',
    instances: 34,
    severity: 'major',
    impact: 'medium',
    affectedFiles: 18
  },
  {
    category: 'Deprecated APIs',
    instances: 8,
    severity: 'critical',
    impact: 'high',
    affectedFiles: 12
  },
  {
    category: 'Poor Test Coverage',
    instances: 28,
    severity: 'major',
    impact: 'medium',
    affectedFiles: 45
  }
];

// Pie chart data for debt distribution
const debtDistribution = [
  { title: 'Critical Issues', value: 12, color: '#ff0000' },
  { title: 'Major Issues', value: 28, color: '#ffa500' },
  { title: 'Minor Issues', value: 45, color: '#ffff00' }
];

const TechnicalDebtAnalyzer = () => {
  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Analysis of technical debt and modernization priorities"
        >
          Technical Debt Analysis
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Summary Metrics */}
        <ColumnLayout columns={3} variant="text-grid">
          <Box
            padding="s"
            textAlign="center"
            variant="awsui-key-label"
          >
            <SpaceBetween size="xs">
              <Box variant="h3">
                {debtMetrics.overallScore}/100
              </Box>
              <Box>Code Health Score</Box>
            </SpaceBetween>
          </Box>
          <Box
            padding="s"
            textAlign="center"
            variant="awsui-key-label"
          >
            <SpaceBetween size="xs">
              <Box variant="h3">
                {debtMetrics.debtRatio}%
              </Box>
              <Box>Technical Debt Ratio</Box>
            </SpaceBetween>
          </Box>
          <Box
            padding="s"
            textAlign="center"
            variant="awsui-key-label"
          >
            <SpaceBetween size="xs">
              <Box variant="h3">
                {debtMetrics.criticalIssues + debtMetrics.majorIssues}
              </Box>
              <Box>Critical & Major Issues</Box>
            </SpaceBetween>
          </Box>
        </ColumnLayout>

        {/* Debt Distribution Chart */}
        <Container
          header={
            <Header variant="h3">
              Issue Distribution
            </Header>
          }
        >
          <ColumnLayout columns={2}>
            <PieChart
              data={debtDistribution}
              detailPopoverContent={(datum, sum) => [
                { key: 'Issues', value: datum.value },
                { key: 'Percentage', value: `${((datum.value / sum) * 100).toFixed(1)}%` }
              ]}
              size="medium"
              variant="donut"
              innerMetricDescription="Total Issues"
              innerMetricValue={debtDistribution.reduce((sum, item) => sum + item.value, 0)}
            />
            <Box>
              <SpaceBetween size="s">
                <Box variant="h4">AI Recommendations</Box>
                <Box variant="small">
                  • Prioritize resolving the {debtMetrics.criticalIssues} critical issues
                </Box>
                <Box variant="small">
                  • Focus on high-impact areas first
                </Box>
                <Box variant="small">
                  • Consider breaking down complex methods
                </Box>
                <Link href="#">View detailed recommendations</Link>
              </SpaceBetween>
            </Box>
          </ColumnLayout>
        </Container>

        {/* Technical Debt Categories Table */}
        <Table
          columnDefinitions={[
            {
              id: 'category',
              header: 'Issue Category',
              cell: item => item.category,
              sortingField: 'category'
            },
            {
              id: 'severity',
              header: 'Severity',
              cell: item => (
                <StatusIndicator type={
                  item.severity === 'critical' ? 'error' :
                  item.severity === 'major' ? 'warning' : 'info'
                }>
                  {item.severity}
                </StatusIndicator>
              )
            },
            {
              id: 'instances',
              header: 'Instances',
              cell: item => item.instances
            },
            {
              id: 'impact',
              header: 'Business Impact',
              cell: item => (
                <StatusIndicator type={
                  item.impact === 'high' ? 'error' :
                  item.impact === 'medium' ? 'warning' : 'info'
                }>
                  {item.impact}
                </StatusIndicator>
              )
            },
            {
              id: 'affectedFiles',
              header: 'Affected Files',
              cell: item => item.affectedFiles
            }
          ]}
          items={debtCategories}
          sortingColumn={{ sortingField: 'instances', sortingDescending: true }}
          header={
            <Header
              variant="h3"
              counter={`(${debtCategories.length})`}
            >
              Technical Debt Categories
            </Header>
          }
          variant="container"
          stickyHeader
          stripedRows
        />

        {/* Quick Actions */}
        <Box textAlign="center">
          <SpaceBetween size="xs" direction="horizontal">
            <Link href="#">Export Detailed Report</Link>
            <Link href="#">View Trend Analysis</Link>
            <Link href="#">Schedule Review</Link>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Container>
  );
};

export default TechnicalDebtAnalyzer;
